// Service pour gérer les mousses

import { historiqueService } from './HistoriqueService';
import { apiFetch } from './httpClient';

/**
 * Interface pour les mousses
 */
export interface Mousse {
  id?: string;
  nom: string;
  prixSaisi: number;
  dimensionsPlaque: {
    longueur: number;
    largeur: number;
    epaisseur: number;
  };
  typePrix: 'HT_NET' | 'TTC_PUBLIC';
  remiseFournisseur?: number;
  coefficientMarge?: number;
  prixM3?: number;
  fournisseur?: string;
  dateCreation?: string;
  dateModification?: string;
}

/**
 * Interface pour la saisie de mousse
 */
export interface MousseSaisie {
  nom: string;
  prixSaisi: number;
  longueur: number;
  largeur: number;
  epaisseur: number;
  typePrix: 'HT_NET' | 'TTC_PUBLIC';
  remiseFournisseur?: number;
  coefficientMarge?: number;
  fournisseur?: string;
}

/**
 * Classe pour gérer les mousses
 */
class MousseService {

  /**
   * Calcule le prix au m³ selon la logique commerciale
   */
  private calculerPrixM3(mousseSaisie: MousseSaisie): number {
    // Volume en m³
    const volume = (mousseSaisie.longueur / 100) * 
                   (mousseSaisie.largeur / 100) * 
                   (mousseSaisie.epaisseur / 100);
    
    if (mousseSaisie.typePrix === 'HT_NET') {
      // Saisie HT NET + coefficient de marge
      const coeffMarge = mousseSaisie.coefficientMarge || 1.5;
      const prixVentePlaque = mousseSaisie.prixSaisi * coeffMarge;
      return prixVentePlaque / volume;
      
    } else {
      // Saisie TTC PUBLIC - conversion en HT
      const prixVenteM3TTC = mousseSaisie.prixSaisi / volume;
      return prixVenteM3TTC / 1.2;  // Conversion TTC → HT
    }
  }

  /**
   * Calcule le prix d'achat pour les commandes fournisseur
   * Gère à la fois les mousses frontend (prixSaisi) et backend PostgreSQL (prixM3)
   */
  public calculerPrixCommande(mousse: Mousse): number {
    // ✅ CORRECTION : Gérer les mousses PostgreSQL (prixM3)
    if (mousse.prixM3) {
      // Mousse depuis PostgreSQL : prixM3 est déjà le prix au m³
      return mousse.prixM3;
    }
    
    // Mousse frontend classique
    if (mousse.typePrix === 'HT_NET') {
      // Prix HT NET saisi (sans marge)
      return mousse.prixSaisi;
      
    } else {
      // Prix TTC PUBLIC avec remise fournisseur
      const remise = mousse.remiseFournisseur || 0;
      return (mousse.prixSaisi / 1.2) * (1 - remise / 100);
    }
  }

  /**
   * Convertit une mousse API (PostgreSQL) vers le format frontend
   */
  private mapApiMousse(apiMousse: any): Mousse {
    const prixM3 = Number(apiMousse.prix_metre_cube ?? apiMousse.prixM3 ?? 0);
    const densite = apiMousse.densite !== undefined ? Number(apiMousse.densite) : undefined;
    const epaisseur = apiMousse.epaisseur !== undefined ? Number(apiMousse.epaisseur) : 0;

    return {
      id: apiMousse.reference || apiMousse.id,
      nom: apiMousse.nom,
      prixSaisi: prixM3,
      dimensionsPlaque: {
        longueur: Number(apiMousse.longueur || 0),
        largeur: Number(apiMousse.largeur || 0),
        epaisseur: epaisseur,
      },
      typePrix: 'HT_NET',
      remiseFournisseur: apiMousse.remiseFournisseur,
      coefficientMarge: apiMousse.coefficientMarge,
      prixM3: prixM3,
      fournisseur: apiMousse.fournisseur,
      dateCreation: apiMousse.created_at,
      dateModification: apiMousse.updated_at,
    };
  }

  /**
   * Récupère la liste des mousses
   */
  async getMousses(): Promise<Mousse[]> {
    try {
      const data = await apiFetch<any[]>('/mousses');
      return (data || []).map((m: any) => this.mapApiMousse(m));
    } catch (error) {
      console.error('Erreur lors de la récupération des mousses:', error);
      console.warn('⚠️ [MOUSSE SERVICE] API mousses indisponible, fallback démo');
      return this.getMoussesDemos();
    }
  }

  /**
   * Sauvegarde une mousse
   */
  async saveMousse(mousseSaisie: MousseSaisie): Promise<Mousse> {
    try {
      // Calculer le prix au m³
      const prixM3 = this.calculerPrixM3(mousseSaisie);

      const body = {
        reference: `mousse_${Date.now()}`,
        nom: mousseSaisie.nom,
        prixM3: Math.round(prixM3 * 100) / 100,
        fournisseur: mousseSaisie.fournisseur,
        densite: mousseSaisie.remiseFournisseur,
        epaisseur: mousseSaisie.epaisseur,
      };

      const payload = await apiFetch<any>('/mousses', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const created = payload.created || payload;
      const mapped = this.mapApiMousse(created);

      // Historique
      try {
        await historiqueService.enregistrerModification(
          'mousses',
          `Ajout de la mousse: ${mapped.nom}`,
          'Utilisateur'
        );
      } catch (historyError) {
        console.warn('⚠️ Erreur lors de l\'enregistrement dans l\'historique:', historyError);
      }

      return mapped;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la mousse:', error);
      throw error;
    }
  }

  /**
   * Met à jour une mousse existante
   */
  async updateMousse(id: string, mousseSaisie: MousseSaisie): Promise<Mousse> {
    try {
      // Calculer le prix au m³
      const prixM3 = this.calculerPrixM3(mousseSaisie);

      const body = {
        nom: mousseSaisie.nom,
        prixM3: Math.round(prixM3 * 100) / 100,
        fournisseur: mousseSaisie.fournisseur,
        densite: mousseSaisie.remiseFournisseur,
        epaisseur: mousseSaisie.epaisseur,
      };

      const payload = await apiFetch<any>(`/mousses/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });

      const after = payload.after || payload;
      const mapped = this.mapApiMousse(after);

      // Historique
      try {
        await historiqueService.enregistrerModification(
          'mousses',
          `Modification de la mousse: ${mapped.nom}`,
          'Utilisateur'
        );
      } catch (historyError) {
        console.warn('⚠️ Erreur lors de l\'enregistrement dans l\'historique:', historyError);
      }

      return mapped;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la mousse:', error);
      throw error;
    }
  }

  /**
   * Supprime une mousse
   */
  async deleteMousse(id: string): Promise<void> {
    try {
      const payload = await apiFetch<any>(`/mousses/${id}`, {
        method: 'DELETE',
      });

      const mousseToDelete = payload.deleted ? this.mapApiMousse(payload.deleted) : undefined;
      
      // Enregistrer dans l'historique
      if (mousseToDelete) {
        try {
          await historiqueService.enregistrerModification(
            'mousses',
            `Suppression de la mousse: ${mousseToDelete.nom}`,
            'Utilisateur'
          );
        } catch (historyError) {
          console.warn('⚠️ Erreur lors de l\'enregistrement dans l\'historique:', historyError);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la mousse:', error);
      throw error;
    }
  }

  /**
   * Récupère une mousse par son ID depuis l'API backend
   */
  async getMousseById(id: string): Promise<Mousse | null> {
    try {
      const mousse = await apiFetch<any>(`/mousses/${id}`);
      console.log('✅ [MOUSSE SERVICE] Mousse récupérée depuis API:', mousse);
      return this.mapApiMousse(mousse);
    } catch (error) {
      console.error('Erreur lors de la récupération de la mousse:', error);
      console.warn('⚠️ [MOUSSE SERVICE] API échouée, fallback démo');
      const mousses = await this.getMoussesDemos();
      return mousses.find(m => m.id === id) || null;
    }
  }

  /**
   * Retourne des mousses de démonstration
   */
  private getMoussesDemos(): Mousse[] {
    return [
      {
        id: 'mousse_001',
        nom: '35A/10 MOUSSE BULTEX 35 A',
        prixSaisi: 260.47,
        dimensionsPlaque: {
          longueur: 160,
          largeur: 200,
          epaisseur: 10
        },
        typePrix: 'HT_NET',
        coefficientMarge: 2,
        prixM3: 1627.94,
        fournisseur: 'Bultex France',
        dateCreation: '2025-01-15',
        dateModification: '2025-01-15'
      },
      {
        id: 'mousse_002',
        nom: '40B/15 MOUSSE DUNLOPILLO 40 B',
        prixSaisi: 625.12,
        dimensionsPlaque: {
          longueur: 160,
          largeur: 200,
          epaisseur: 10
        },
        typePrix: 'TTC_PUBLIC',
        remiseFournisseur: 50,
        prixM3: 1627.93,
        fournisseur: 'Dunlopillo Pro',
        dateCreation: '2025-01-15',
        dateModification: '2025-01-15'
      },
      {
        id: 'mousse_003',
        nom: '30A/8 MOUSSE STANDARD 30 A',
        prixSaisi: 180.50,
        dimensionsPlaque: {
          longueur: 200,
          largeur: 120,
          epaisseur: 8
        },
        typePrix: 'HT_NET',
        coefficientMarge: 1.8,
        prixM3: 1693.75,
        fournisseur: 'Mousse Direct',
        dateCreation: '2025-01-15',
        dateModification: '2025-01-15'
      }
    ];
  }
}

// Exporter une instance unique du service
export const mousseService = new MousseService();
